import { useEffect } from 'react';

/**
 * SecurityHeaders component that implements enhanced security monitoring
 * Note: CSP is handled by Lovable's platform to prevent conflicts
 */
export const SecurityHeaders = () => {

  useEffect(() => {
    // Set additional security headers via meta tags
    const setSecurityHeaders = () => {
      // Remove any existing security headers
      const existingHeaders = document.querySelectorAll('meta[name^="security-"]');
      existingHeaders.forEach(header => header.remove());

      // X-Content-Type-Options
      const contentTypeOptions = document.createElement('meta');
      contentTypeOptions.httpEquiv = 'X-Content-Type-Options';
      contentTypeOptions.content = 'nosniff';
      contentTypeOptions.name = 'security-content-type';
      document.head.appendChild(contentTypeOptions);

      // X-Frame-Options - Removed to allow embedding calendar iframes

      // X-XSS-Protection
      const xssProtection = document.createElement('meta');
      xssProtection.httpEquiv = 'X-XSS-Protection';
      xssProtection.content = '1; mode=block';
      xssProtection.name = 'security-xss-protection';
      document.head.appendChild(xssProtection);

      // Referrer Policy
      const referrerPolicy = document.createElement('meta');
      referrerPolicy.name = 'referrer';
      referrerPolicy.content = 'strict-origin-when-cross-origin';
      document.head.appendChild(referrerPolicy);

      // Permissions Policy
      const permissionsPolicy = document.createElement('meta');
      permissionsPolicy.httpEquiv = 'Permissions-Policy';
      permissionsPolicy.content = [
        'camera=()',
        'microphone=()',
        'geolocation=()',
        'interest-cohort=()',
        'payment=()',
        'usb=()'
      ].join(', ');
      permissionsPolicy.name = 'security-permissions-policy';
      document.head.appendChild(permissionsPolicy);

      // Strict Transport Security (HSTS) - only effective when served over HTTPS
      if (window.location.protocol === 'https:') {
        const hsts = document.createElement('meta');
        hsts.httpEquiv = 'Strict-Transport-Security';
        hsts.content = 'max-age=31536000; includeSubDomains; preload';
        hsts.name = 'security-hsts';
        document.head.appendChild(hsts);
      }

      // Cross-Origin policies removed to allow embedding external calendar iframes
    };

    setSecurityHeaders();

    // Enhanced security event listeners
    const handleSecurityEvents = () => {
      // Detect and log security-related events
      
      // Monitor for potential XSS attempts
      const originalConsoleError = console.error;
      console.error = (...args: any[]) => {
        const message = args.join(' ').toLowerCase();
        if (message.includes('script') || message.includes('eval') || message.includes('unsafe')) {
          // Log potential XSS attempt
          console.warn('Potential XSS attempt detected:', message);
        }
        originalConsoleError.apply(console, args);
      };

      // Monitor for CSP violations
      document.addEventListener('securitypolicyviolation', (event) => {
        console.warn('CSP Violation:', {
          directive: event.violatedDirective,
          blockedURI: event.blockedURI,
          lineNumber: event.lineNumber,
          columnNumber: event.columnNumber,
          sourceFile: event.sourceFile
        });

        // Report CSP violations to monitoring service in production
        if (import.meta.env.PROD) {
          fetch('https://kpaixcycljzsxxriyzjh.supabase.co/functions/v1/csp-violation', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              directive: event.violatedDirective,
              blockedURI: event.blockedURI,
              userAgent: navigator.userAgent,
              timestamp: new Date().toISOString()
            })
          }).catch(() => {
            // Fail silently - don't want security monitoring to break the app
          });
        }
      });

      // Monitor for suspicious navigation attempts
      let navigationAttempts = 0;
      const resetNavigationCounter = () => { navigationAttempts = 0; };
      
      window.addEventListener('beforeunload', () => {
        navigationAttempts++;
        if (navigationAttempts > 10) {
          console.warn('Suspicious navigation pattern detected');
          // Could implement additional security measures here
        }
      });

      // Reset counter every minute
      setInterval(resetNavigationCounter, 60000);

      // Monitor for rapid form submissions (potential bot activity)
      let formSubmissions = 0;
      const resetFormCounter = () => { formSubmissions = 0; };
      
      document.addEventListener('submit', () => {
        formSubmissions++;
        if (formSubmissions > 5) {
          console.warn('Rapid form submission detected - potential bot activity');
        }
      });

      // Reset form counter every 30 seconds
      setInterval(resetFormCounter, 30000);
    };

    handleSecurityEvents();

    // Cleanup function
    return () => {
      const securityHeaders = document.querySelectorAll('meta[name^="security-"]');
      securityHeaders.forEach(header => header.remove());
    };
  }, []);

  // This component doesn't render anything visible
  return null;
};